import React, {useMemo} from 'react';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import TeamIcon from '@mui/icons-material/Groups3';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {ChecklistItemCategory} from '../../api/Model';
import EmployeeLink from '../../components/employee-link/EmployeeLink';
import EmployeeAvatar from '../../components/employee-avatar/EmployeeAvatar';
import {ResolvedNewJoiner} from '../../api/ResolvedNewJoinerStore';
import TeamComponent from "../../components/team-component/TeamComponent";
import {NewJoinerRemoteTag, NewJoinerTypeTag} from "./NewJoinerTypeTag";
import {NewJoinerApi, NewJoinerOperations} from "./NewJoinerApi";
import {NeighbourhoodChecklistGroup} from "./NeighbourhoodChecklistGroup";
import {KeyChecklistGroup} from "./KeyChecklistGroup";
import {WelcomePackChecklistGroup} from "./WelcomePackChecklistGroup";
import {isoDate, WeekDay, withDayOfWeek} from "../../util/DateUtil";
import {getOrSet} from "../../util/MapUtil";
import {stringCmp} from "../../util/SortUtil";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Virtuoso} from "react-virtuoso";

type NewJoinerWeekGroup = {
    key: string;
    joiners: ResolvedNewJoiner[];
    from: string;
    to: string;
};

function groupByJoinWeek(rows: ResolvedNewJoiner[]): NewJoinerWeekGroup[] {
    const groups = new Map<string, NewJoinerWeekGroup>();

    rows.forEach(newJoiner => {
        const monday = withDayOfWeek(new Date(newJoiner.joinDate), WeekDay.Monday);
        const key = isoDate(monday);
        const group = getOrSet(groups, key, () => ({
            key,
            joiners: [],
            from: newJoiner.joinDate,
            to: newJoiner.joinDate,
        }));
        group.joiners.push(newJoiner);
        group.from = newJoiner.joinDate < group.from ? newJoiner.joinDate : group.from;
        group.to = newJoiner.joinDate > group.to ? newJoiner.joinDate : group.to;
    });

    return [...groups.values()]
        .sort(stringCmp('key'));
}


function NewJoinerCardTopLine({newJoiner}: {
    newJoiner: ResolvedNewJoiner,
}) {
    return (
        <CardContent sx={{py: 1}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <NewJoinerTypeTag type={newJoiner.type}/>
                    <NewJoinerRemoteTag remote={newJoiner.remote}/>
                </Stack>
                <Typography variant="caption">Joins {newJoiner.joinDate}</Typography>
            </Stack>
        </CardContent>
    );
}

function NewJoinerCardHeader({newJoiner, operations}: {
    newJoiner: ResolvedNewJoiner,
    operations: NewJoinerOperations,
}) {

    return (
        <CardHeader
            avatar={
                newJoiner.person.type === "existing"
                    ? <EmployeeAvatar employee={newJoiner.person.employee}/>
                    : (
                        <Avatar sx={{bgcolor: theme => theme.palette.action.disabledBackground, width: 44, height: 44}}>
                            <PsychologyAltIcon/>
                        </Avatar>
                    )
            }
            title={
                newJoiner.person.type === "existing" ? (
                    <EmployeeLink employee={newJoiner.person.employee} variant="subtitle1"/>
                ) : (
                    <Typography variant="subtitle1" sx={{fontWeight: 600, lineHeight: 1.25}}>
                        {newJoiner.person.fullName}
                    </Typography>
                )
            }
            subheader={newJoiner.employee?.jobTitle}
            action={(operations.handleEdit || operations.handleDelete) &&
                <Stack direction="row" sx={{mt: 0.5}}>
                    {operations.handleEdit && (
                        <Tooltip title="Edit">
                            <IconButton color="primary" size="small" onClick={operations.handleEdit}>
                                <EditIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {operations.handleDelete && (
                        <Tooltip title="Delete">
                            <IconButton color="error" size="small" onClick={operations.handleDelete}>
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            }
            sx={{pb: 0.5, height: "72px"}}
        />
    );
}

function NewJoinerAboutCardContent({newJoiner}: {
    newJoiner: ResolvedNewJoiner;
}) {
    return (
        <CardContent sx={{pt: 0.5, pb: 1, minHeight: "68px"}}>
            <Stack sx={{columnGap: 2, rowGap: 1}} direction="row" flexWrap="wrap">
                {(newJoiner.employee?.teamPath) && (
                    <Stack direction="row" alignItems="center" gap={1} flexGrow={1}>
                        <Tooltip title="Team">
                            <TeamIcon color="disabled"/>
                        </Tooltip>
                        <TeamComponent
                            teamPath={newJoiner.employee.teamPath}
                            slotProps={{wrapper: {sx: {width: undefined}}}}
                        />
                    </Stack>
                )}
                {newJoiner.manager && (
                    <Stack direction="row" alignItems="center" gap={1} flexGrow={1}>
                        <Tooltip title="Manager">
                            <SupervisorAccountIcon color="disabled"/>
                        </Tooltip>
                        <EmployeeAvatar employee={newJoiner.manager} sx={{width: 16, height: 16}}/>
                        <EmployeeLink employee={newJoiner.manager} variant="body2"/>
                    </Stack>
                )}
                {newJoiner.notes && (
                    <Tooltip title={newJoiner.notes}>
                        <Typography variant="body2" color="text.secondary" sx={{fontStyle: "italic", height: 24, overflow: "hidden", textWrap: "nowrap", textOverflow: "ellipsis"}}>
                            {newJoiner.notes}
                        </Typography>
                    </Tooltip>
                )}
            </Stack>
        </CardContent>
    );
}

function StandaloneChecklistItemBlock({newJoiner, operations}: {
    newJoiner: ResolvedNewJoiner,
    operations: NewJoinerOperations,
}) {
    const toggles = useMemo(
        () => operations.checklistToggles[ChecklistItemCategory.Standalone],
        [operations.checklistToggles],
    );

    return (
        <>{toggles.map(toggle => (
            <Box key={toggle.item.id}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={newJoiner.employee && toggle.checked}
                            onChange={toggle.toggle}
                            disabled={toggle.disabled}
                            sx={{px: 1, py: 0}}
                        />
                    }
                    label={
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="inherit">
                                {toggle.item.label}
                            </Typography>

                            {toggle.item.link && (
                                <IconButton
                                    component="a"
                                    href={toggle.item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    sx={{width: 24, height: 24}}
                                >
                                    <OpenInNewIcon sx={{fontSize: 16}}/>
                                </IconButton>
                            )}
                        </Stack>
                    }
                    disabled={!newJoiner.employee}
                    color="primary"
                />
            </Box>
        ))}</>
    );
}

function NewJoinerChecklistPane({newJoiner, operations}: {
    newJoiner: ResolvedNewJoiner,
    operations: NewJoinerOperations,
}) {
    return (
        <CardContent sx={{pt: 1.5, pb: 2, flex: 1}}>
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{display: "block", lineHeight: 1, mb: 1.25, fontSize: "0.65rem", letterSpacing: "0.08em"}}
            >
                Onboarding Checklist
            </Typography>

            <Stack spacing={1}>
                {!newJoiner.remote && (
                    <NeighbourhoodChecklistGroup
                        newJoiner={newJoiner}
                        operations={operations}
                    />
                )}

                {!newJoiner.remote && (
                    <KeyChecklistGroup
                        newJoiner={newJoiner}
                        operations={operations}
                    />
                )}

                <WelcomePackChecklistGroup
                    newJoiner={newJoiner}
                    operations={operations}
                />

                <StandaloneChecklistItemBlock
                    newJoiner={newJoiner}
                    operations={operations}
                />
            </Stack>
        </CardContent>
    );
}

function NewJoinerCard({newJoiner, api}: {
    newJoiner: ResolvedNewJoiner,
    api: NewJoinerApi,
}) {
    const operations = useMemo(
        () => api.forNewJoiner(newJoiner),
        [api, newJoiner],
    );

    return (
        <Card variant="outlined">
            <NewJoinerCardTopLine
                newJoiner={newJoiner}
            />
            <Divider/>
            <NewJoinerCardHeader
                newJoiner={newJoiner}
                operations={operations}
            />
            <NewJoinerAboutCardContent newJoiner={newJoiner}/>
            <Divider/>
            <NewJoinerChecklistPane
                newJoiner={newJoiner}
                operations={operations}
            />
        </Card>
    );
}

export function NewJoinerTable({rows, api}: {
    rows: ResolvedNewJoiner[],
    api: NewJoinerApi,
}) {
    const groupedRows = useMemo(
        () => groupByJoinWeek(rows),
        [rows],
    );

    return (
        <Virtuoso
            data={groupedRows}
            useWindowScroll
            computeItemKey={(_, group) => group.key}
            itemContent={(_, group) => (
                <Box pb={3}>
                    <Typography variant="h4" component="h2" sx={{mb: 1.5, mt: 2}}>
                        {group.from === group.to ? group.from : `${group.from} – ${group.to}`}
                    </Typography>
                    <Grid container spacing={2}>
                        {group.joiners.map(newJoiner => (
                            <Grid key={newJoiner.id} size={{xs: 12, md: 6, lg: 4}}>
                                <NewJoinerCard
                                    newJoiner={newJoiner}
                                    api={api}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        />
    );
}
